import { arrayMove } from '@dnd-kit/sortable';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnSizingState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Table,
  type Updater,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
} from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type {
  DataTableProps,
  DataTableState,
} from '~/components/common/data-table/data-table.types';
import { SelectionCheckbox } from '~/components/common/selection-checkbox/selection-checkbox';
import { Button } from '~/components/ui/button';
import { TableBody, TableCell, TableRow } from '~/components/ui/table';
import { useHeaderScrollOffset } from '~/hooks/use-header-scroll-offset';
import { useControllableState } from '~/hooks/useControllableState';
import { cn } from '~/utils/cn';

import { DataTableHeader } from './data-table-header';
import { DataTablePagination } from './data-table-pagination';
import {
  isFirstRightPinned,
  isLastLeftPinned,
  pinnedCellStyle,
  pinnedLeftVar,
} from './data-table-pinning';
import { DataTableSkeleton } from './data-table-skeleton';
import { DataTableToolbar } from './data-table-toolbar';

/**
 * Resolves a TanStack Table Updater<T> (value or setter function) to the new value.
 * TanStack Table state handlers receive either a direct value or a `(prev) => next` function.
 */
function applyUpdater<T>(updater: Updater<T>, current: T): T {
  return typeof updater === 'function' ? (updater as (old: T) => T)(current) : updater;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * A fully-featured data table built on TanStack Table and shadcn's Table primitives.
 *
 * Supports client-side and server-side data management, with opt-in features:
 * sorting, column filtering, global search, row selection with bulk actions,
 * and a paginator with configurable page sizes.
 *
 * **Server-side note:** `onStateChange` is called directly inside each event handler
 * (sort click, filter input, page change) — not in a `useEffect`. This prevents
 * infinite render loops and guarantees the callback fires only on user interaction.
 *
 * @template T - The row data type.
 *
 * @example Client-side
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   data={rows}
 *   rowKey={(r) => r.id}
 *   enableSorting
 *   enableGlobalFilter
 * />
 * ```
 *
 * @example Server-side
 * ```tsx
 * <DataTable
 *   mode="server"
 *   columns={columns}
 *   data={rows}
 *   rowKey={(r) => r.id}
 *   totalRows={500}
 *   isLoading={isFetching}
 *   enableSorting
 *   enableFiltering
 *   onStateChange={useCallback((state) => fetchRows(state), [])}
 * />
 * ```
 */
export function DataTable<T>({
  columns: columnDefs,
  data,
  rowKey,
  pageSize: initialPageSize = 20,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  isLoading = false,
  skeletonRows = 5,
  emptyMessage = 'No results found.',
  mode = 'client',
  autoResetPageIndex,
  totalRows,
  onStateChange,
  enableSorting = false,
  enableFiltering = false,
  enableGlobalFilter = false,
  enableRowSelection = false,
  revealSelectionOnHover = false,
  clearSelectionOnPageChange = false,
  onSelectionChange,
  rowSelection: rowSelectionProp,
  onRowSelectionChange: onRowSelectionChangeProp,
  enableColumnResizing = false,
  bulkActions,
  onRowClick,
  getRowClassName,
  renderSubRow,
  paginationLabels,
  hidePageNumbers = false,
  hidePageSizeSelector = false,
  hidePagination = false,
  maxHeight,
  fillHeight = false,
  paginationClassName,
  paginationExtra,
  tableItemType,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  pagination: paginationProp,
  onPaginationChange: onPaginationChangeProp,
  pinnedLeftColumnId,
  pinnedRightColumnId,
  enableColumnReordering = false,
  columnOrder: columnOrderProp,
  onColumnOrderChange: onColumnOrderChangeProp,
  testId = 'data-table',
}: DataTableProps<T>) {
  const [sorting, setSorting] = useControllableState<SortingState>({
    value: sortingProp,
    onChange: onSortingChangeProp,
    defaultValue: [],
  });
  const [columnOrder, setColumnOrder] = useControllableState<string[]>({
    value: columnOrderProp,
    onChange: onColumnOrderChangeProp,
    defaultValue: [],
  });
  // Pinning is fixed by the caller (users can't unpin), so it's derived, not
  // stateful — an empty left list when no column is pinned. The injected
  // selection and expand columns are frozen WITH the pinned one, ahead of it:
  // left unpinned, they scrolled under the frozen column and it slid over them.
  const columnPinning = useMemo(
    () => ({
      left: pinnedLeftColumnId
        ? [
            ...(enableRowSelection ? ['__select__'] : []),
            ...(renderSubRow ? ['__expand__'] : []),
            pinnedLeftColumnId,
          ]
        : [],
      right: pinnedRightColumnId ? [pinnedRightColumnId] : [],
    }),
    [pinnedLeftColumnId, pinnedRightColumnId, enableRowSelection, renderSubRow],
  );
  // Frozen columns stick at the MEASURED right edge of the ones before them: each pinned
  // header cell's rendered width is summed into `--dt-pin-left-<i>` on the table (read by
  // `pinnedCellStyle`), re-measured whenever one of them resizes.
  const tableRef = useRef<HTMLTableElement>(null);
  // The scroll container: its scrollbar starts under the sticky header (`scroll-under-header`).
  const scrollRef = useRef<HTMLDivElement>(null);
  useHeaderScrollOffset(scrollRef);
  const pinnedLeftKey = columnPinning.left.join('|');
  useLayoutEffect(() => {
    const tableEl = tableRef.current;
    const ids = pinnedLeftKey ? pinnedLeftKey.split('|') : [];
    if (!tableEl || ids.length === 0) return;
    const cells = ids.map((id) =>
      tableEl.querySelector<HTMLElement>(`thead th[data-column-id="${CSS.escape(id)}"]`),
    );
    // A header cell not rendered yet: keep the size arithmetic (no variables set).
    const found = cells.filter((cell): cell is HTMLElement => cell !== null);
    if (found.length !== ids.length) return;
    const measure = () => {
      let left = 0;
      found.forEach((cell, index) => {
        tableEl.style.setProperty(pinnedLeftVar(index), `${String(left)}px`);
        left += cell.getBoundingClientRect().width;
      });
    };
    // Measured offsets are cleared when the pinned set changes, so a column never sticks at
    // an offset measured for a previous set (the size arithmetic takes over until re-measured).
    const clear = () => {
      found.forEach((_, index) => {
        tableEl.style.removeProperty(pinnedLeftVar(index));
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return clear;
    const observer = new ResizeObserver(measure);
    found.forEach((cell) => {
      observer.observe(cell);
    });
    return () => {
      observer.disconnect();
      clear();
    };
  }, [pinnedLeftKey, isLoading, data.length]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  // User-dragged widths, keyed by column id. Empty until the first drag, at which
  // point TanStack falls back to each column's `size` — so this stays `{}` (and
  // costs nothing) on every table that leaves resizing off.
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  // Controllable like sorting and pagination, so a page can own the selection (and
  // clear it) when its bulk UI lives in its own header rather than the toolbar —
  // see the `rowSelection` prop docs.
  const [rowSelection, setRowSelection] = useControllableState<RowSelectionState>({
    value: rowSelectionProp,
    onChange: onRowSelectionChangeProp,
    defaultValue: {},
  });
  const [pagination, setPagination] = useControllableState<PaginationState>({
    value: paginationProp,
    onChange: onPaginationChangeProp,
    defaultValue: { pageIndex: 0, pageSize: initialPageSize },
  });

  // Keyed by `rowKey` rather than row index so expansion survives sorting,
  // filtering, and pagination changes that reorder or re-slice the rows.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());

  // Toggles a row's expanded state by its stable key.
  function toggleExpanded(key: string): void {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Server-side state emission
  // Builds the DataTableState payload from current state + caller-supplied overrides.
  // Called synchronously inside each event handler — never inside useEffect.
  // ---------------------------------------------------------------------------
  function emitState(
    overrides: Partial<{
      sorting: SortingState;
      columnFilters: ColumnFiltersState;
      globalFilter: string;
      pageIndex: number;
      pageSize: number;
    }>,
  ): void {
    if (mode !== 'server' || !onStateChange) return;

    const s = overrides.sorting ?? sorting;
    const f = overrides.columnFilters ?? columnFilters;
    const g = overrides.globalFilter ?? globalFilter;
    const pi = overrides.pageIndex ?? pagination.pageIndex;
    const ps = overrides.pageSize ?? pagination.pageSize;

    const state: DataTableState = {
      page: pi + 1,
      pageSize: ps,
      sorting: s.map((item) => ({ id: item.id, desc: item.desc })),
      columnFilters: f.map((item) => ({ id: item.id, value: String(item.value) })),
      globalFilter: g,
    };

    onStateChange(state);
  }

  // ---------------------------------------------------------------------------
  // State-change handlers — each updates local state AND emits to the parent.
  // Sorting and filtering reset the page to 1 in server mode.
  // ---------------------------------------------------------------------------

  function handleSortingChange(updater: Updater<SortingState>): void {
    const newSorting = applyUpdater(updater, sorting);
    setSorting(newSorting);
    if (mode === 'server') {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      emitState({ sorting: newSorting, pageIndex: 0 });
    }
  }

  function handleColumnFiltersChange(updater: Updater<ColumnFiltersState>): void {
    const newFilters = applyUpdater(updater, columnFilters);
    setColumnFilters(newFilters);
    if (mode === 'server') {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      emitState({ columnFilters: newFilters, pageIndex: 0 });
    }
  }

  function handleGlobalFilterChange(value: string): void {
    setGlobalFilter(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    emitState({ globalFilter: value, pageIndex: 0 });
  }

  // Single funnel for every selection write, so `onSelectionChange` can never go stale:
  // resolving the selected rows from `data` (keyed by `rowKey`, same as `getRowId`) keeps
  // this independent of the row model, which in server mode holds only the current page.
  function applyRowSelection(next: RowSelectionState): void {
    setRowSelection(next);
    onSelectionChange?.(data.filter((row) => next[rowKey(row)]));
  }

  // Opt-in page-scoped selection — see `clearSelectionOnPageChange` for why a
  // cross-page selection under-reports in server mode. Off by default so tables that
  // rely on selection surviving a page round-trip keep that behaviour.
  function clearSelectionIfPageScoped(): void {
    if (clearSelectionOnPageChange) applyRowSelection({});
  }

  function handlePageChange(page: number): void {
    const newPageIndex = page - 1;
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
    clearSelectionIfPageScoped();
    emitState({ pageIndex: newPageIndex });
  }

  function handlePageSizeChange(size: number): void {
    setPagination({ pageIndex: 0, pageSize: size });
    clearSelectionIfPageScoped();
    emitState({ pageIndex: 0, pageSize: size });
  }

  // ---------------------------------------------------------------------------
  // Column definitions — map DataTableColumn<T> → TanStack ColumnDef<T>
  // ---------------------------------------------------------------------------
  const columns = useMemo<ColumnDef<T>[]>(() => {
    const dataCols: ColumnDef<T>[] = columnDefs.map((col) => ({
      id: col.id,
      // String headers pass through; a ReactNode header (e.g. a select-all
      // checkbox) is wrapped in a render fn so it satisfies TanStack's
      // `StringOrTemplateHeader` type. `flexRender` handles both forms.
      header: typeof col.header === 'string' ? col.header : () => col.header,
      // accessorFn is required for sorting/filtering to work on the column value.
      // Defaults to () => undefined for display-only columns (no sort/filter effect).
      accessorFn: (col.accessorFn ?? ((): undefined => undefined)) as (row: T) => unknown,
      cell: ({ row }) => col.cell(row.original),
      enableSorting: col.enableSorting ?? enableSorting,
      enableColumnFilter: col.enableFiltering ?? enableFiltering,
      // Only forwarded when the consumer gave one — TanStack's own 150px default is
      // a better fallback than a fabricated number, and `size` is inert unless
      // `enableColumnResizing` puts it into an inline style.
      ...(col.width !== undefined ? { size: col.width } : {}),
      meta: {
        ...(col.className !== undefined ? { className: col.className } : {}),
        ...(col.headerClassName !== undefined ? { headerClassName: col.headerClassName } : {}),
        ...(col.filterOptions !== undefined ? { filterOptions: col.filterOptions } : {}),
      },
    }));

    // Chevron toggle for expandable rows. Injected automatically so consumers
    // don't have to reserve a column for it.
    const expandCol: ColumnDef<T> = {
      id: '__expand__',
      header: '',
      cell: ({ row }) => {
        const key = rowKey(row.original);
        const isExpanded = expandedKeys.has(key);
        return (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 cursor-pointer"
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            aria-expanded={isExpanded}
            data-testid={`${testId}-expand-button-${key}`}
            // Stop propagation so expanding never also triggers `onRowClick`.
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(key);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      // Sized as well as classed: with resizing on, `getTotalSize()` sums `size`, so
      // leaving this at TanStack's 150px default would reserve 150px of table width
      // for a 32px chevron.
      size: 32,
      enableResizing: false,
      meta: { className: 'w-8' },
    };

    const withExpand = renderSubRow ? [expandCol, ...dataCols] : dataCols;

    if (!enableRowSelection) return withExpand;

    // Hover-reveal classes for `revealSelectionOnHover`. Only the checkbox fades —
    // the column keeps its width either way (opacity doesn't affect layout), so
    // nothing shifts on hover. The checkbox stays in the tab order and keeps its
    // accessible name while faded: `has-[:focus-visible]:opacity-100` brings it back
    // for keyboard users rather than hiding a reachable control. The `has-[]` forms
    // target the label wrapper, since the state lives on the hidden input inside it.
    //
    // `hoverVariant` differs by row: the select-all box reveals on hover anywhere over
    // the table (`group/table` on the scroll container), each row's box on that row's
    // hover (the plain `group` already set on every TableRow).
    //
    // Returns undefined — no fade at all — when the feature is off, or once anything is
    // selected, so the controls that clear a selection can never hide behind a hover.
    const revealClass = (t: Table<T>, hoverVariant: string): string | undefined => {
      if (!revealSelectionOnHover) return undefined;
      if (t.getIsSomeRowsSelected() || t.getIsAllRowsSelected()) return undefined;
      return cn(
        'opacity-0 transition-opacity has-[:checked]:opacity-100 has-[:focus-visible]:opacity-100 has-[:indeterminate]:opacity-100',
        hoverVariant,
      );
    };

    // Prepend the checkbox selection column — injected automatically, not defined by consumers.
    //
    // `SelectionCheckbox` rather than the 16px Radix `Checkbox`: a <label> that fills
    // the whole cell with a 44px minimum hit target (WCAG 2.5.8), so a near-miss
    // still ticks the box instead of firing `onRowClick` on the row behind it. The
    // cell drops its padding (`p-0` via `meta.className`) so the label really is
    // the cell, header and body alike.
    const selectionCol: ColumnDef<T> = {
      id: '__select__',
      // The select-all checkbox lives in this column's header so header and body
      // stay one-cell-per-column aligned (a separate manual <th> would double it).
      header: ({ table: t }) => (
        <SelectionCheckbox
          checked={t.getIsAllPageRowsSelected()}
          // Partial page selection shows the bar; a click from that state reports
          // `true` (native indeterminate → checked), i.e. "select the rest".
          indeterminate={!t.getIsAllPageRowsSelected() && t.getIsSomePageRowsSelected()}
          onCheckedChange={(next) => {
            t.toggleAllPageRowsSelected(next);
          }}
          label="Select all rows on this page"
          testId={`${testId}-select-all-checkbox`}
          className={revealClass(t, 'group-hover/table:opacity-100')}
        />
      ),
      cell: ({ row, table: t }) => (
        <SelectionCheckbox
          checked={row.getIsSelected()}
          onCheckedChange={(next) => {
            row.toggleSelected(next);
          }}
          label="Select row"
          testId={`${testId}-row-checkbox-${row.id}`}
          // The label is the click target, so this is where `onRowClick` has to be
          // stopped — otherwise ticking a box also fires the row action. Never
          // preventDefault here: that cancels the label's click forwarding to the
          // input and nothing toggles.
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={revealClass(t, 'group-hover:opacity-100')}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      // The 44px hit target plus a little air, not the 150px default that
      // `getTotalSize()` would otherwise reserve for it. `meta.className` reaches
      // both <th> and <td>, which is what lets the label fill the cell edge to edge.
      size: 48,
      enableResizing: false,
      meta: { className: 'w-12 min-w-12 max-w-12 p-0' },
    };

    return [selectionCol, ...withExpand];
  }, [
    columnDefs,
    enableSorting,
    enableFiltering,
    enableRowSelection,
    revealSelectionOnHover,
    renderSubRow,
    expandedKeys,
    rowKey,
    testId,
  ]);

  // ---------------------------------------------------------------------------
  // TanStack Table instance
  // ---------------------------------------------------------------------------
  const serverRowCount = mode === 'server' ? (totalRows ?? data.length) : undefined;

  // eslint-disable-next-line react-hooks/incompatible-library -- known TanStack Table + React Compiler limitation
  const table = useReactTable<T>({
    data,
    columns,
    // Spread rowCount only when defined — exactOptionalPropertyTypes disallows passing undefined
    ...(serverRowCount !== undefined ? { rowCount: serverRowCount } : {}),
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
      pagination,
      columnSizing,
      columnOrder,
      columnPinning,
    },
    getRowId: (row) => rowKey(row),
    enableColumnResizing,
    // `onChange` tracks the pointer live rather than snapping on release — a table
    // this wide needs to show the reflow while you drag, or you are aiming blind.
    columnResizeMode: 'onChange',
    // Floor only. No ceiling: a 214-character GPC path is a legitimate thing to widen
    // a column for, and the scroll container already bounds the result.
    defaultColumn: { minSize: 60 },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: (updater) => {
      applyRowSelection(applyUpdater(updater, rowSelection));
    },
    onPaginationChange: setPagination,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: (updater) => {
      setColumnOrder(applyUpdater(updater, columnOrder));
    },
    // Client-side model helpers (no-ops when manual* flags are set)
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Server-side: disable internal computation, parent provides pre-processed data
    manualSorting: mode === 'server',
    manualFiltering: mode === 'server',
    manualPagination: mode === 'server',
    // Spread only when set — exactOptionalPropertyTypes disallows passing undefined.
    ...(autoResetPageIndex !== undefined ? { autoResetPageIndex } : {}),
  });

  // Commits a drag-reorder: moves `activeId` to `overId`'s slot. Works off the
  // current order (all leaf columns when none is set yet), so hidden columns keep
  // their place; pinning still forces the pinned column to the left regardless.
  function handleColumnReorder(activeId: string, overId: string): void {
    if (activeId === overId) return;
    const base = columnOrder.length > 0 ? columnOrder : table.getAllLeafColumns().map((c) => c.id);
    const from = base.indexOf(activeId);
    const to = base.indexOf(overId);
    if (from === -1 || to === -1) return;
    setColumnOrder(arrayMove(base, from, to));
  }

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  const rows = table.getRowModel().rows;
  const totalPages = table.getPageCount();
  const currentPage = pagination.pageIndex + 1;

  const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);

  const resolvedTotalRows = mode === 'server' ? (totalRows ?? 0) : data.length;

  // Show the footer whenever there are rows, not only when they overflow one
  // page. Hiding it on a single page also hid the rows-per-page selector, which
  // trapped the user at a large page size: once everything fit, there was no
  // control left to reduce it. The page-number nav still hides — see
  // `hidePageNumbers` below.
  const showPagination = !hidePagination && !isLoading && resolvedTotalRows > 0;

  const visibleColumnCount = table.getVisibleLeafColumns().length;

  // Cells actually rendered per row, including the resize-mode surplus absorber (see
  // DataTableHeader). Anything spanning the full row — skeleton, empty state, sub-row —
  // must count it, or those rows come up one cell short of the header.
  const spannedColumnCount = visibleColumnCount + (enableColumnResizing ? 1 : 0);

  const renderBodyCell = (cell: Cell<T, unknown>) => (
    <TableCell
      key={cell.id}
      data-column-id={cell.column.id}
      className={cn(
        cell.column.columnDef.meta?.className,
        // A pinned cell must stay opaque so the columns scrolling
        // underneath never show through — `dt-pinned-cell` holds an
        // opaque fill at rest and on the row's 50%-alpha hover.
        cell.column.getIsPinned() && 'dt-pinned-cell',
        isLastLeftPinned(cell.column) && 'border-border border-r',
        isFirstRightPinned(cell.column) &&
          'shadow-[inset_1px_0_0_var(--border),-6px_0_8px_-6px_rgb(0_0_0/0.12)]',
      )}
      // Inline width only while resizing (so a table that leaves it
      // off keeps its Tailwind widths), plus sticky offsets when pinned.
      style={{
        ...(enableColumnResizing ? { width: cell.column.getSize() } : {}),
        ...(pinnedCellStyle(cell.column, 'body') ?? {}),
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );

  return (
    <div
      // `isolate`: the sticky header's z-30 (above frozen body cells) stays inside the table,
      // never above a page's own overlay (a loading veil) sitting next to it.
      className={cn('isolate', fillHeight ? 'flex min-h-0 flex-1 flex-col gap-4' : 'space-y-4')}
      data-testid={testId}
    >
      {/* Toolbar: global search + bulk actions */}
      <DataTableToolbar
        enableGlobalFilter={enableGlobalFilter}
        globalFilter={globalFilter}
        onGlobalFilterChange={handleGlobalFilterChange}
        testId={testId}
        bulkActions={
          enableRowSelection && selectedRows.length > 0 && bulkActions
            ? bulkActions(selectedRows)
            : undefined
        }
      />

      {/* Table scroll container. `fillHeight` grows it to fill the parent (the
          sticky header stays pinned as rows scroll); `maxHeight` caps it at a
          fixed height; otherwise it's horizontal-only scroll. */}
      <div
        className={cn(
          // `group/table` is a named Tailwind marker (inert alone) that lets the
          // select-all checkbox reveal on hover anywhere over the table. Named so it
          // never collides with the plain `group` each TableRow already carries.
          'group/table scroll-under-header rounded-md border bg-white',
          fillHeight
            ? 'min-h-0 flex-1 overflow-auto'
            : maxHeight
              ? 'overflow-auto'
              : 'overflow-x-auto',
        )}
        ref={scrollRef}
        style={!fillHeight && maxHeight ? { maxHeight } : undefined}
      >
        {/* Use bare <table> instead of shadcn <Table> — <Table> wraps in a
            <div overflow-auto> that would intercept sticky before our wrapper.

            Resizing swaps the layout algorithm. Auto layout lets the browser
            redistribute widths to fit content, which silently overrides the width a
            drag just set, so resizing needs `table-fixed` to honour the inline widths.

            `minWidth` rather than `width`, and `w-full` kept: the column total can be
            narrower than the content lane (GPC's nine columns come to ~1,520px), and a
            fixed `width` there would leave dead space beside the table. So the wrapper
            scrolls when the columns are wider than the lane, and the table still fills
            it when they aren't.

            That combination only works because of the surplus-absorber cell rendered
            in DataTableHeader and on each row: fixed layout hands leftover width to the
            unsized column, so the measured columns keep rendering at exactly
            `getSize()`. Without it, fixed layout would stretch them proportionally and
            `getSize()` — which is the only thing the drag arithmetic reads — would stop
            matching the rendered box, making the divider lag the cursor. */}
        <table
          ref={tableRef}
          data-testid={`${testId}-table`}
          className={cn('w-full caption-bottom text-sm', enableColumnResizing && 'table-fixed')}
          style={enableColumnResizing ? { minWidth: table.getTotalSize() } : undefined}
        >
          <DataTableHeader
            table={table}
            enableSorting={enableSorting}
            enableFiltering={enableFiltering}
            enableColumnResizing={enableColumnResizing}
            enableColumnReordering={enableColumnReordering}
            onReorder={handleColumnReorder}
            testId={testId}
          />
          <TableBody>
            {isLoading ? (
              <DataTableSkeleton columns={spannedColumnCount} rows={skeletonRows} />
            ) : rows.length === 0 ? (
              <TableRow data-testid={`${testId}-empty`}>
                <TableCell
                  colSpan={spannedColumnCount}
                  className="text-muted-foreground h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const isExpanded = !!renderSubRow && expandedKeys.has(rowKey(row.original));
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      // Lets a consumer resolve a delegated click back to its row and column.
                      data-row-key={rowKey(row.original)}
                      data-testid={`${testId}-row-${row.id}`}
                      // When clickable, the row behaves as a button: keyboard-
                      // focusable with Enter/Space activation and a visible focus
                      // ring, so row-triggered UI (e.g. a detail panel) is fully
                      // keyboard-operable. Inert when `onRowClick` is absent.
                      role={onRowClick ? 'button' : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      // `group` is a Tailwind marker class (inert by itself) so
                      // per-cell content can opt into row-level hover via
                      // `group-hover:` — used by the Experiments table's
                      // hover-revealed Discard action.
                      className={cn(
                        'group',
                        onRowClick &&
                          'focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
                        getRowClassName?.(row.original),
                      )}
                      onClick={
                        onRowClick
                          ? (e) => {
                              // Only activate when the click originates inside the row's
                              // own DOM subtree. A portalled dialog/sheet (e.g. a Radix
                              // overlay opened from an action menu in one of the row's
                              // cells) renders its content elsewhere in the DOM — but
                              // React portals bubble events through the React tree, not
                              // the DOM tree, so the click still reaches this handler.
                              // Checking DOM containment (rather than exact target
                              // identity, as onKeyDown below does) rejects that
                              // portal-originated click while still letting a normal
                              // click anywhere on the row's own cells activate it.
                              if (!e.currentTarget.contains(e.target as Node)) return;
                              onRowClick(row.original);
                            }
                          : undefined
                      }
                      onKeyDown={
                        onRowClick
                          ? (e) => {
                              // Only the row's OWN key events activate it. Without this,
                              // typing a space in any control inside the row (an input in
                              // a cell, or a portalled dialog/sheet — React portals bubble
                              // through the React tree) triggered the row action, and the
                              // preventDefault below swallowed the keystroke so the space
                              // never reached the field. Native buttons behave this way too.
                              // (The click handler above guards the same portal-bubbling
                              // hazard with a DOM-containment check instead, since a normal
                              // click's target is a cell descendant, never the row itself.)
                              if (e.target !== e.currentTarget) return;
                              // Space would otherwise scroll the page; Enter/Space
                              // both activate, matching native button semantics.
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick(row.original);
                              }
                            }
                          : undefined
                      }
                    >
                      {row
                        .getVisibleCells()
                        .filter((cell) => cell.column.getIsPinned() !== 'right')
                        .map(renderBodyCell)}
                      {/* Matches the header's surplus absorber so the column count lines up;
                          before any right-pinned column, as in DataTableHeader. */}
                      {enableColumnResizing && <TableCell aria-hidden="true" className="p-0" />}
                      {row
                        .getVisibleCells()
                        .filter((cell) => cell.column.getIsPinned() === 'right')
                        .map(renderBodyCell)}
                    </TableRow>
                    {isExpanded && (
                      <TableRow
                        className="hover:bg-transparent"
                        data-testid={`${testId}-sub-row-${row.id}`}
                      >
                        <TableCell colSpan={spannedColumnCount} className="bg-muted/30 p-4">
                          {renderSubRow(row.original)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && (
        <DataTablePagination
          page={currentPage}
          totalPages={totalPages}
          totalRows={resolvedTotalRows}
          pageSize={pagination.pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hidePageNumbers={hidePageNumbers || totalPages <= 1}
          hidePageSizeSelector={hidePageSizeSelector}
          testId={testId}
          {...(paginationClassName !== undefined ? { className: paginationClassName } : {})}
          {...(paginationExtra !== undefined ? { extra: paginationExtra } : {})}
          {...(paginationLabels !== undefined ? { labels: paginationLabels } : {})}
          {...(tableItemType !== undefined ? { tableItemType } : {})}
        />
      )}
    </div>
  );
}
